import React, { useMemo } from 'react';
import { View, StyleSheet, ViewStyle, TouchableOpacity } from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';

interface CarouselDotsProps {
  count: number;
  activeIndex: number;
  activeColor?: string;
  inactiveColor?: string;
  containerStyle?: ViewStyle;
  dotsWrapperStyle?: ViewStyle;
  onDotPress?: (index: number) => void;
  showBackground?: boolean;
}

export const CarouselDots: React.FC<CarouselDotsProps> = ({
  count,
  activeIndex,
  activeColor,
  inactiveColor,
  containerStyle,
  dotsWrapperStyle,
  onDotPress,
  showBackground = true,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      position: 'absolute',
      bottom: 20,
      left: 0,
      right: 0,
      alignItems: 'center',
    },
    dotsWrapper: {
      flexDirection: 'row',
      gap: 6,
      backgroundColor: mc.carouselBg,
      paddingHorizontal: 8,
      paddingVertical: 8,
      borderRadius: 20,
    },
    dotsWrapperNoBg: {
      backgroundColor: 'transparent',
      paddingHorizontal: 0,
      paddingVertical: 0,
    },
    dot: {
      width: 7,
      height: 7,
      borderRadius: 3.5,
    },
  }), [mc]);

  // These defaults used to derive the active ink from the scrim, because the
  // light theme paired a dark scrim with near-black dots and the two cancelled
  // out. That is fixed at the token level now — `components.carousel` is
  // internally consistent in both themes and `themeContrast.test.ts` fails the
  // build if it stops being — so the designed tokens are used directly again,
  // which also keeps this carousel identical to the other consumers of them.
  const resolvedActive = activeColor ?? mc.dotActive;
  const resolvedInactive = inactiveColor ?? mc.dotInactive;

  if (count <= 1) return null;

  const renderDot = (index: number) => {
    const dotElement = (
      <View
        style={[
          styles.dot,
          { backgroundColor: index === activeIndex ? resolvedActive : resolvedInactive },
        ]}
      />
    );

    if (onDotPress) {
      return (
        <TouchableOpacity
          key={index}
          onPress={() => onDotPress(index)}
          activeOpacity={0.7}
          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
        >
          {dotElement}
        </TouchableOpacity>
      );
    }

    return <View key={index}>{dotElement}</View>;
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={[
        styles.dotsWrapper, 
        !showBackground && styles.dotsWrapperNoBg,
        dotsWrapperStyle,
      ]}>
        {Array.from({ length: count }).map((_, index) => renderDot(index))}
      </View>
    </View>
  );
};
